import { useEffect, useState } from "react";
import { api, fmtNumber, parseApiError } from "@/lib/api";
import { PageHeader } from "./AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { DestructiveConfirmDialog } from "@/components/ui/destructive-confirm-dialog";
import { DataStatePanel, DataStateRow } from "@/components/ui/data-state";
import { ChefHat, Plus, Trash2, Calculator, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function Kitchen() {
  const [recipes, setRecipes] = useState([]);
  const [skus, setSkus] = useState([]);
  const [mrp, setMrp] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", finished_sku_id: "", yield_qty: 1, waste_pct: 0, items: [{ ingredient_sku_id: "", qty: 0, unit: "" }] });
  const [recipesLoading, setRecipesLoading] = useState(true);
  const [recipesError, setRecipesError] = useState("");
  const [mrpLoading, setMrpLoading] = useState(false);
  const [mrpError, setMrpError] = useState("");

  const load = async () => {
    setRecipesLoading(true);
    setRecipesError("");
    try {
      const r = await api.get("/recipes");
      setRecipes(r.data);
    } catch (e) {
      setRecipesError(parseApiError(e, "Không tải được danh sách công thức").message);
    } finally {
      setRecipesLoading(false);
    }
  };
  useEffect(() => {
    load();
    api.get("/master/skus").then((r) => setSkus(r.data));
  }, []);

  const computeMRP = async (days = 7) => {
    setMrpLoading(true);
    setMrpError("");
    try { const r = await api.get(`/mrp/demand?days=${days}`); setMrp(r.data); toast.success("Đã tính MRP"); }
    catch (e) {
      const message = parseApiError(e, "MRP lỗi").message;
      setMrpError(message);
      toast.error(message);
    } finally { setMrpLoading(false); }
  };

  const save = async () => {
    try {
      await api.post("/recipes", { ...form, items: form.items.filter((i) => i.ingredient_sku_id) });
      toast.success("Đã lưu công thức"); setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Lỗi"); }
  };

  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, { ingredient_sku_id: "", qty: 0, unit: "" }] }));
  const updateItem = (i, k, v) => setForm((f) => ({ ...f, items: f.items.map((it, idx) => idx === i ? { ...it, [k]: v } : it) }));
  const removeItem = (i) => setForm((f) => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));

  const remove = async (id) => {
    await api.delete(`/recipes/${id}`); toast.success("Đã xóa"); load();
  };

  return (
    <div data-testid="page-kitchen">
      <PageHeader
        kicker="P3 • Vận hành"
        title="Bếp tổng • BOM & MRP"
        breadcrumb={["Vận hành", "Bếp tổng"]}
        actions={
          <>
            <Button onClick={() => computeMRP(7)} variant="outline" className="rounded-sm" data-testid="btn-mrp"><Calculator className="w-4 h-4 mr-1.5" />Tính MRP 7 ngày</Button>
            <Button onClick={() => setOpen(true)} className="bg-emerald-800 hover:bg-emerald-900 rounded-sm" data-testid="btn-create-recipe"><Plus className="w-4 h-4 mr-1.5" />Thêm công thức</Button>
          </>
        }
      />
      <div className="p-6 lg:p-8">
        <Tabs defaultValue="recipes">
          <TabsList className="bg-stone-100 rounded-sm">
            <TabsTrigger value="recipes" className="rounded-sm data-[state=active]:bg-white" data-testid="tab-recipes">Công thức (BOM)</TabsTrigger>
            <TabsTrigger value="mrp" className="rounded-sm data-[state=active]:bg-white" data-testid="tab-mrp">MRP - Demand & Shortage</TabsTrigger>
          </TabsList>

          <TabsContent value="recipes" className="mt-3 space-y-3">
            {!recipesLoading && !recipesError && recipes.map((r) => (
              <div key={r.id} className="bg-white border border-stone-200 rounded-sm p-4" data-testid={`recipe-${r.id}`}>
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-stone-500 font-bold">{r.finished_code || "—"}</div>
                    <h3 className="font-serif text-lg font-bold flex items-center gap-2"><ChefHat className="w-4 h-4 text-amber-700" />{r.name}</h3>
                    <div className="text-xs text-stone-500 mt-0.5">Sản lượng: <span className="font-mono">{r.yield_qty}</span> • Hao hụt: <span className="font-mono">{r.waste_pct}%</span></div>
                  </div>
                  <DestructiveConfirmDialog
                    title="Xóa công thức?"
                    description={`Công thức ${r.name || r.finished_code || r.id} sẽ bị xóa.`}
                    confirmLabel="Xóa công thức"
                    token={String(r.finished_code || r.name || r.id)}
                    onConfirm={() => remove(r.id)}
                    trigger={(
                      <button className="text-stone-400 hover:text-red-700"><Trash2 className="w-4 h-4" /></button>
                    )}
                  />
                </div>
                <div className="border-t border-stone-100 pt-2">
                  <div className="text-[10px] uppercase tracking-wider text-stone-500 font-bold mb-1">Nguyên liệu</div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5 text-xs">
                    {(r.items || []).map((it) => (
                      <div key={it.id} className="bg-stone-50 border border-stone-100 rounded-sm px-2 py-1">
                        <span className="font-mono text-stone-500">{it.ing_code}</span> {it.ing_name} — <span className="font-bold">{fmtNumber(it.qty)} {it.unit || ""}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
            {recipesLoading && <DataStatePanel type="loading" message="Đang tải danh sách công thức..." />}
            {!recipesLoading && recipesError && <DataStatePanel type="error" message={recipesError} onRetry={load} />}
            {!recipesLoading && !recipesError && recipes.length === 0 && <DataStatePanel type="empty" message="Chưa có công thức nào" />}
          </TabsContent>

          <TabsContent value="mrp" className="mt-3">
            {mrpLoading ? (
              <DataStatePanel type="loading" message="Đang tính nhu cầu nguyên liệu..." />
            ) : mrpError ? (
              <DataStatePanel type="error" message={mrpError} onRetry={() => computeMRP(7)} />
            ) : mrp ? (
              <div className="space-y-4">
                <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-3 text-sm">
                  Dựa trên doanh số <strong>{mrp.days}</strong> ngày gần nhất. Các nguyên liệu thiếu hụt sẽ hiển thị màu đỏ.
                </div>
                <div className="bg-white border border-stone-200 rounded-sm overflow-hidden">
                  <div className="px-4 py-2 border-b bg-stone-50">
                    <h4 className="font-semibold text-sm">Nhu cầu nguyên liệu</h4>
                  </div>
                  <table className="w-full text-sm">
                    <thead className="bg-stone-50">
                      <tr>{["Mã","Tên","Đơn vị","Cần dùng","Tồn","Thiếu"].map(h => <th key={h} className="text-left px-4 py-2 text-[10px] uppercase tracking-wider text-stone-600 font-bold">{h}</th>)}</tr>
                    </thead>
                    <tbody className="divide-y divide-stone-100">
                      {mrp.ingredients.map((ing, i) => (
                        <tr key={i} className={ing.shortage > 0 ? "bg-red-50" : ""}>
                          <td className="px-4 py-2 font-mono text-xs">{ing.code}</td>
                          <td className="px-4 py-2 font-semibold">{ing.name}</td>
                          <td className="px-4 py-2">{ing.unit}</td>
                          <td className="px-4 py-2 font-mono">{fmtNumber(ing.needed)}</td>
                          <td className="px-4 py-2 font-mono">{fmtNumber(ing.stock)}</td>
                          <td className="px-4 py-2 font-mono font-bold">
                            {ing.shortage > 0 ? <span className="text-red-700 flex items-center gap-1"><AlertTriangle className="w-3 h-3" />{fmtNumber(ing.shortage)}</span> : <span className="text-emerald-700">đủ</span>}
                          </td>
                        </tr>
                      ))}
                      {mrp.ingredients.length === 0 && <DataStateRow colSpan={6} type="empty" message="Chưa có dữ liệu MRP — cần có recipe + POS orders" />}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="text-center text-stone-400 py-10">Bấm <strong>Tính MRP</strong> để xem nhu cầu nguyên liệu từ POS demand</div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-2xl">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Tạo công thức BOM</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Tên công thức *</Label>
                <Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="h-9 rounded-sm mt-1" data-testid="f-recipe-name" />
              </div>
              <div>
                <Label className="text-xs">SP thành phẩm *</Label>
                <Select value={form.finished_sku_id} onValueChange={(v) => setForm({...form, finished_sku_id: v})}>
                  <SelectTrigger className="h-9 rounded-sm mt-1"><SelectValue placeholder="Chọn" /></SelectTrigger>
                  <SelectContent>{skus.map((s) => <SelectItem key={s.id} value={s.id}>{s.code} - {s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Sản lượng</Label><Input type="number" value={form.yield_qty} onChange={(e) => setForm({...form, yield_qty: e.target.value})} className="h-9 rounded-sm mt-1" /></div>
              <div><Label className="text-xs">Hao hụt %</Label><Input type="number" value={form.waste_pct} onChange={(e) => setForm({...form, waste_pct: e.target.value})} className="h-9 rounded-sm mt-1" /></div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs">Nguyên liệu</Label>
                <Button onClick={addItem} size="sm" variant="ghost" className="text-emerald-800 h-7"><Plus className="w-3 h-3 mr-1" />Thêm</Button>
              </div>
              {form.items.map((it, i) => (
                <div key={i} className="grid grid-cols-12 gap-1.5 mb-1.5">
                  <Select value={it.ingredient_sku_id} onValueChange={(v) => updateItem(i, "ingredient_sku_id", v)}>
                    <SelectTrigger className="h-9 rounded-sm col-span-6"><SelectValue placeholder="Nguyên liệu" /></SelectTrigger>
                    <SelectContent>{skus.map((s) => <SelectItem key={s.id} value={s.id}>{s.code} - {s.name}</SelectItem>)}</SelectContent>
                  </Select>
                  <Input type="number" placeholder="SL" value={it.qty} onChange={(e) => updateItem(i, "qty", e.target.value)} className="h-9 rounded-sm col-span-3" />
                  <Input placeholder="Đv (kg, ml...)" value={it.unit} onChange={(e) => updateItem(i, "unit", e.target.value)} className="h-9 rounded-sm col-span-2" />
                  <button onClick={() => removeItem(i)} className="text-stone-400 hover:text-red-600 col-span-1"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Hủy</Button>
            <Button onClick={save} className="bg-emerald-800 hover:bg-emerald-900 rounded-sm" data-testid="btn-save-recipe">Lưu</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
